import {gql} from '@apollo/client';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getSharedSmartChipProps, SharedSmartChipProps} from 'ui-library/chips/ChipUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {BasicEvent} from 'util/Types';

import {
  AnalysisChipPopoverQuery,
  AnalysisChipPopoverQueryVariables,
} from './__generated/AnalysisChipPopoverQuery';
import ChipPopoverContent from './ChipPopoverContent';
import {NO_ACCESS_CHIP_TEXT} from './NoAccessChip';
import QueryingChipPopoverContent from './QueryingChipPopoverContent';
import SmartChip from './SmartChip';
import useSmartChipQuery, {SmartChipType} from './useSmartChipQuery';

type PopoverAnalysis = AnalysisChipPopoverQuery['analysis'];

export const ANALYSIS_CHIP_POPOVER_QUERY = gql`
  query AnalysisChipPopoverQuery($analysisId: StrictString!) @skipAudit @engComponent(name: ANALYSIS_TOOL) {
    analysis(id: $analysisId) {
      ...AnalysisChipOrInaccessibleGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('AnalysisChipOrInaccessible').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function AnalysisChipPopoverContent({analysis}: {analysis: PopoverAnalysis}): JSX.Element | null {
  if (isInaccessible(analysis)) {
    return <>{NO_ACCESS_CHIP_TEXT}</>;
  }
  const fields = [
    {
      label: 'Name',
      value: (
        <>
          <BenchlingIcon icon={ModelTypeConstants.ANALYSIS} /> &nbsp; {analysis.name}
        </>
      ),
    },
    {label: 'ID', value: <>{analysis.id}</>},
  ];
  return <ChipPopoverContent info={{fields}} />;
}

export function QueryingAnalysisChipPopoverContent({id}: {id: string}): JSX.Element {
  return (
    <QueryingChipPopoverContent<AnalysisChipPopoverQuery, AnalysisChipPopoverQueryVariables>
      variables={{analysisId: id}}
      query={ANALYSIS_CHIP_POPOVER_QUERY}
    >
      {(data: AnalysisChipPopoverQuery) => <AnalysisChipPopoverContent analysis={data.analysis} />}
    </QueryingChipPopoverContent>
  );
}

export interface AnalysisChipProps extends SharedSmartChipProps<SmartChipType.ANALYSIS> {}

export default function AnalysisChip(props: AnalysisChipProps): JSX.Element {
  const sharedSmartChipProps = getSharedSmartChipProps(props);
  const {id, onClick} = sharedSmartChipProps;

  const {
    item: analysis,
    handleClickLink,
    isInaccessible,
    itemUrl: analysisUrl,
  } = useSmartChipQuery<SmartChipType.ANALYSIS>({
    id,
    onClick,
    getUrlFn: (analysis) => analysis.url,
  });
  const onClickHandler = (e: BasicEvent): void => {
    if (analysis) {
      if (onClick != null) {
        handleClickLink(e);
      } else {
        FileBrowserActions.openFile(analysis.id);
      }
    }
  };
  return (
    <SmartChip
      {...sharedSmartChipProps}
      icon={ModelTypeConstants.ANALYSIS}
      isInaccessible={isInaccessible}
      item={analysis}
      onClick={onClickHandler}
      url={analysisUrl}
      popoverContentOverride={id ? <QueryingAnalysisChipPopoverContent id={id} /> : null}
    />
  );
}
